/**
 * Providers and credentials — Owner only.
 *
 * Visual Reference: `design/screens/settings-credentials.html` — states
 * default, error, loading, empty.
 *
 * ONE DELIBERATE DEVIATION FROM THE MOCKUP, and one that CLOSED at Story 18.5:
 *
 *   1. **"Check this model works" renders since Story 18.5** — it did not
 *      before, and that absence was DECISION-016 rather than an oversight: the
 *      control fires a test EXTRACTION, which needs `services/ai`, AI Gateway
 *      routing and model-output validation, the whole M2 slice DECISION-021
 *      cut. A button that cannot do what the mockup shows it doing reads as
 *      broken rather than as not-yet-built. `POST …/{id}/check-model` was
 *      declared M2 in `docs/api-contract.md` from the start and answers now, so
 *      the control arrives with the thing behind it — and the e2e test that
 *      asserted its absence moved in the same commit rather than being deleted.
 *
 *   2. **The saved list shows the five BYOK providers and not the mail
 *      credential.** One row has one owner: Story 11.3's integrations screen
 *      renders "Email (Mailgun) … with status, last-used and masked
 *      fingerprint", and a credential appearing on two screens is two places to
 *      replace it from and two places for the state to disagree. It also keeps
 *      the empty state honest — "You're using Leadgible's AI" would be a lie on
 *      an organisation that had configured mail and no model key.
 *
 * THE FORM POSTS `fields`, NOT A BARE VALUE. Story 11.1's API takes an object
 * keyed by the names the provider declares — `{ apiKey }` here, `{ apiKey,
 * domain }` for mail — because a credential is not always one string. This
 * screen only ever sends the one field, and sends it under its name.
 *
 * BASE URL IS CONDITIONALLY REQUIRED, which the mockup could not show in a
 * single state: `azure` and `other` have no public default endpoint, so the API
 * refuses them without one (Story 11.1). The label carries "— optional" or
 * "— required" accordingly rather than letting the person find out by being
 * refused.
 *
 * THE BASE URL HELPER DOES NOT SAY "or your own endpoint" — Story 18.4 and
 * DECISION-136. The decision withdrew the self-hosted promise and the API
 * refuses such an endpoint, so a line inviting an operator into that refusal
 * is the narrowing being invisible at the one place somebody decides. The build
 * said so before the drawing did; the drawing caught up at Story 25.1.
 *
 * NOTHING HERE HAS A VALUE TO HIDE. `PublicCredential` structurally lacks the
 * secret, so there is no "reveal" state to suppress and no masked field to
 * guard — the fingerprint is the whole of what arrives (RULE-049).
 *
 * Story 11.2.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
/**
 * The threshold and its arithmetic come from `@leadgible/schema` — DECISION-086.
 * Story 12.2's needs-attention feed composes `credentials` (rotation due)
 * SERVER-SIDE, so the number has two readers in two packages and the API
 * cannot import this one.
 */
import { CREDENTIAL_PROVIDERS, providerLabel, rotationDueAt } from '@leadgible/schema'
import { apiDelete, apiGet, apiPost, ApiFailure } from '../../lib/api'
import { useMe } from '../../shell/useMe'
import { Button } from '../../ui/Button'
import { Field } from '../../ui/Field'
import { Pill } from '../../ui/Pill'
import { Select } from '../../ui/Select'
import { Skeleton } from '../../ui/Skeleton'

/** As `PublicCredential` leaves the API — fingerprint and timestamps only. */
type Credential = {
  id: string
  provider: string
  label: string | null
  fingerprint: string
  baseUrl: string | null
  lastUsedAt: number | null
  lastRotatedAt: number | null
  createdAt: number
}

/**
 * The five the mockup's select offers, in its order.
 *
 * `id` is what `credentials.provider` stores and what Story 11.1's registry
 * accepts; the label is what a person is shown. The registry deliberately
 * carries no display names — a provider entry is a tester plus its fields, and
 * a registry that also knew what to call things would be what every later
 * feature reached into.
 *
 * THEY MOVED TO `packages/schema` IN STORY 12.2, and this screen now reads them
 * from there. The needs-attention feed writes "Your OpenAI key hasn't been
 * rotated in 11 months" SERVER-SIDE, and `schema ← api ← app` means the API
 * cannot import this file — the same argument DECISION-086 made about the
 * rotation threshold, about the same table, one story later.
 */
const PROVIDERS = CREDENTIAL_PROVIDERS

const PROVIDER_IDS: readonly string[] = PROVIDERS.map((p) => p.id)

const labelFor = providerLabel

const day = (at: number) =>
  new Date(at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })

/**
 * "12 minutes ago", "6 days ago", "11 months ago" — the three shapes the
 * mockup renders, from one function.
 */
function ago(at: number, now: number): string {
  const elapsed = Math.max(0, now - at)
  const minutes = Math.floor(elapsed / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`
  const years = Math.floor(days / 365)
  return `${years} year${years === 1 ? '' : 's'} ago`
}

/** The row's second line, exactly as the mockup composes it. */
function detailOf(credential: Credential, now: number): string {
  const parts = [credential.fingerprint, `Added ${day(credential.createdAt)}`]
  parts.push(
    credential.lastUsedAt === null
      ? 'Not used yet'
      : `Last used ${ago(credential.lastUsedAt, now)}`,
  )
  return parts.join(' · ')
}

/* ── the page ───────────────────────────────────────────────────────────── */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-24 shadow-mdx-card">
      {children}
    </section>
  )
}

export function Credentials() {
  const me = useMe()
  const org = me.status === 'ready' ? (me.me.orgs[0] ?? null) : null
  const orgId = org?.id ?? null
  const isOwner = org?.role === 'owner'

  const [credentials, setCredentials] = useState<Credential[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  const [provider, setProvider] = useState<string>(PROVIDERS[0].id)
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const keyRef = useRef<HTMLInputElement>(null)

  const now = Date.now()
  const rule = PROVIDERS.find((p) => p.id === provider)?.baseUrl ?? 'optional'

  const load = useCallback(async () => {
    if (!orgId || !isOwner) return
    setLoading(true)
    try {
      const body = await apiGet<{ credentials: Credential[] }>(`/v1/orgs/${orgId}/credentials`)
      setCredentials(body.credentials.filter((c) => PROVIDER_IDS.includes(c.provider)))
      setFailed(false)
    } catch {
      setCredentials(null)
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [orgId, isOwner])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Save is create-or-replace on (org, provider) — Story 11.1 keeps ONE row per
   * provider, which is what makes "Replace" the word PRD §3.16 uses rather than
   * "add another". So there is one form, and using it twice for OpenAI replaces
   * the OpenAI key rather than growing a second one.
   */
  const save = async () => {
    if (!orgId || saving) return
    // The button is live on an empty field because the mockup draws it that
    // way, so the empty case is answered HERE — in the field the fix goes in,
    // and without spending a request to be told the same thing.
    if (apiKey.trim() === '') {
      setFieldError('Paste the API key first.')
      keyRef.current?.focus()
      return
    }
    setFieldError(null)
    setSaving(true)
    try {
      await apiPost(`/v1/orgs/${orgId}/credentials`, {
        provider,
        fields: { apiKey },
        ...(baseUrl.trim() === '' ? {} : { base_url: baseUrl.trim() }),
      })
      setApiKey('')
      setBaseUrl('')
      await load()
    } catch (thrown) {
      /**
       * THE MESSAGE IS THE SERVER'S, VERBATIM. Story 11.1's test-on-save
       * returns a sentence naming the fix — "That API key was rejected. Check
       * you copied the whole key…" — and `ApiFailure.message` carries it
       * unchanged. The mockup renders exactly this shape: the field flagged,
       * the message beneath it saying what to fix. A message this screen
       * reworded would be one that could disagree with the server's.
       */
      setFieldError(
        thrown instanceof ApiFailure
          ? thrown.message
          : "That key couldn't be checked just now. Try again in a moment.",
      )
    } finally {
      setSaving(false)
    }
  }

  /** Replace prefills the provider and puts the cursor where the key goes. */
  const replace = (credential: Credential) => {
    setProvider(credential.provider)
    setBaseUrl(credential.baseUrl ?? '')
    setApiKey('')
    setFieldError(null)
    keyRef.current?.focus()
    keyRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }

  const remove = async (credential: Credential) => {
    if (!orgId || busyId) return
    setBusyId(credential.id)
    try {
      await apiDelete(`/v1/orgs/${orgId}/credentials/${credential.id}`)
      await load()
    } catch {
      await load()
    } finally {
      setBusyId(null)
    }
  }

  const header = (
    <header className="flex flex-col gap-mdx-8">
      <h1 className="m-0 text-mdx-h1 font-mdx-bold leading-mdx-tight tracking-mdx-h1">
        Providers and credentials
      </h1>
    </header>
  )

  /**
   * The static reassurance line, above everything.
   *
   * The mockup's loading note is explicit that it stays put: "The reassurance
   * note above stays readable, because it is static copy rather than data." So
   * it renders in every state below, including the one where the list failed.
   */
  const note = (
    <div className="flex items-start gap-mdx-16 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-14">
      <span
        aria-hidden="true"
        className="flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm bg-mdx-line text-mdx-14 font-mdx-bold text-mdx-slate"
      >
        •
      </span>
      <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
        Keys are stored encrypted and can't be shown again after you save them. You can replace one
        at any time.
      </span>
    </div>
  )

  /* ── the locked door ──────────────────────────────────────────────────── */

  /**
   * RULE-006, verbatim: "credential access is Owner-only. No other role can
   * view the credentials settings page, **masked or otherwise**."
   *
   * So an Admin gets a locked door rather than the page — no form, no list, no
   * request. The API refuses them independently (`lib/route-contract.ts`
   * declares three of the four O:owner and `services/authz` enforces it), which
   * is the enforcement; this is the explanation, and it exists because a 403
   * with an empty screen reads as a fault rather than as a rule.
   */
  if (me.status === 'ready' && !isOwner) {
    return (
      <>
        {header}
        <section className="flex flex-col items-center gap-mdx-14 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-28 py-mdx-48 text-center shadow-mdx-card">
          <Pill tone="neutral">Owner only</Pill>
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
            Only the owner can see this organisation's credentials
          </span>
          <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
            That is true of the fingerprints as well as the keys themselves. Ask your owner if a
            provider needs adding or replacing.
          </span>
        </section>
      </>
    )
  }

  /* ── the form ─────────────────────────────────────────────────────────── */

  /**
   * The card header the mockup draws twice: a 17px 600 name on the left and a
   * 13px slate note on the right, baseline-aligned and wrapping at 375. "Owner
   * only" is that note — plain text, not a filled badge, which is what the
   * export renders and therefore what this is.
   */
  const cardHeader = (title: string, note: string) => (
    <div className="flex flex-wrap items-baseline justify-between gap-x-mdx-16 gap-y-mdx-8">
      <h2 className="m-0 text-mdx-17 font-mdx-semibold tracking-mdx-h3">{title}</h2>
      <span className="text-mdx-13 text-mdx-slate">{note}</span>
    </div>
  )

  const form = (
    <Card>
      {cardHeader('Bring your own AI key', 'Owner only')}

      {/**
       * TWO COLUMNS, and the Base URL spans both — `grid-template-columns:
       * repeat(2, minmax(0, 1fr))` with `grid-column: 1 / -1` on the third
       * field, straight off the export. It collapses to one column at 375
       * because the columns are `minmax(0, 1fr)` inside a container that is
       * itself narrower than two fields.
       */}
      <div className="grid grid-cols-1 gap-mdx-16 sm:grid-cols-2">
        <Select
          label="Provider"
          showLabel
          value={provider}
          onChange={(event) => {
            setProvider(event.currentTarget.value)
            setFieldError(null)
          }}
        >
          {PROVIDERS.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </Select>

        <Field
          inputRef={keyRef}
          label="API key"
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={apiKey}
          onChange={(event) => {
            setApiKey(event.currentTarget.value)
            setFieldError(null)
          }}
          helper="Write-only. Once saved, you'll only ever see the last four characters."
          error={fieldError}
        />

        <div className="sm:col-span-2">
          <Field
            label={
              <>
                Base URL{' '}
                <span className="font-mdx-regular text-mdx-slate">
                  {rule === 'required' ? '— required' : '— optional'}
                </span>
              </>
            }
            type="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://your-endpoint.example.com/v1"
            value={baseUrl}
            onChange={(event) => setBaseUrl(event.currentTarget.value)}
            /*
              AS DRAWN since Story 25.1's second ingest (2026-10-01). Until then
              this line was a deliberate deviation — Story 18.4, DECISION-136.

              The export drew "For Azure, OpenRouter, Groq or your own
              endpoint", and it was right when it was drawn: PRD §3.12 promised
              all four. DECISION-136 amended that sentence and dropped
              self-hosted, because reaching an arbitrary URL through the AI
              Gateway needs a registered Custom Provider and an account-wide
              `AI Gateway - Edit` token on the Worker — a PRD §1.7 bootstrap
              amendment the decision declined to make.

              So the API REFUSES an endpoint it cannot route to (Story 18.4's
              fourth criterion), and a line promising one would invite the
              operator into exactly that refusal while telling them it would
              work. That is the narrowing being INVISIBLE at the one place
              somebody decides, which is the failure the criterion exists to
              prevent.

              The build changed the sentence first and filed the round-trip the
              mockup owed (BACKLOG, 2026-09-11). Rider 8 of the M3 pass paid
              it: `settings-credentials.html` reads this helper word for word.
              The select's fifth option, `Other`, is still drawn and still
              built, and that half of the BACKLOG line is open.
            */
            helper="For Azure, OpenRouter or Groq"
          />
        </div>
      </div>

      {/**
       * The button and the first sentence share a row and wrap together — the
       * export's `flex-wrap: wrap; align-items: center; gap: 12px 16px`. The
       * button is NOT disabled on an empty field: the mockup draws it solid and
       * clickable in the default state, and an empty submit is answered in the
       * field where the fix is.
       */}
      <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-12">
        <Button onClick={() => void save()} loading={saving ? 'Checking the key…' : undefined}>
          Test and save
        </Button>
        <span className="max-w-mdx-measure-52 text-mdx-13 text-mdx-slate">
          The key is checked against the provider before it's stored, so a bad paste fails here
          rather than at 3am.
        </span>
      </div>

      {/** A footnote, divided from the form above it — the export's own
       *  `padding-top: 16px; border-top: 1px solid --mdx-line-soft`. */}
      <p className="m-0 border-t border-mdx-line-soft pt-mdx-16 text-mdx-13 text-mdx-slate">
        Using your own key removes your monthly analysis limit.
      </p>
    </Card>
  )

  /* ── loading ──────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <>
        {header}
        {note}
        {form}
        <Card>
          <Skeleton className="h-mdx-22 w-mdx-measure-34 max-w-full" />
          {Array.from({ length: 2 }, (_, index) => (
            <div key={index} className="flex flex-col gap-mdx-8">
              <Skeleton className="h-mdx-16 w-mdx-measure-34 max-w-full" />
              <Skeleton className="h-mdx-14 w-full" />
            </div>
          ))}
        </Card>
      </>
    )
  }

  /* ── the list, and the states it can be in ────────────────────────────── */

  const rows = credentials ?? []

  return (
    <>
      {header}
      {note}
      {form}

      <Card>
        {cardHeader('Saved credentials', "Fingerprints only — the stored value can't be displayed")}

        {failed ? (
          <div className="flex flex-col items-center gap-mdx-14 py-mdx-24 text-center">
            <span className="text-mdx-15 font-mdx-semibold">Couldn't load your saved keys</span>
            <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
              Nothing has changed and no key was affected. This list just couldn't be read.
            </span>
            <Button variant="secondary" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : rows.length === 0 ? (
          /**
           * The empty state's own note: "The state names what is true today and
           * what adding one gets them, rather than describing an absence." So
           * it is not "No credentials yet".
           */
          <div className="flex flex-col items-center gap-mdx-14 py-mdx-24 text-center">
            <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3">
              You're using Leadgible's AI
            </span>
            <span className="max-w-mdx-measure-52 text-mdx-15 text-mdx-slate">
              Add your own key to remove your monthly analysis limit.
            </span>
            <Button onClick={() => keyRef.current?.focus()}>Add a key</Button>
          </div>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-mdx-8 p-0">
            {rows.map((credential) => {
              const due = rotationDueAt(credential, now)
              return (
                <li
                  key={credential.id}
                  className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-12 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-14"
                >
                  {/**
                   * The glyph is FILLED, not tinted — `--mdx-hot` or
                   * `--mdx-review` behind white, at `--mdx-radius-control-sm`.
                   * It is the row's status at a glance and the only place the
                   * two states differ before you read the text.
                   */}
                  <span
                    aria-hidden="true"
                    className={`flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm text-mdx-14 font-mdx-bold text-mdx-white ${
                      due ? 'bg-mdx-review' : 'bg-mdx-hot'
                    }`}
                  >
                    {due ? '◐' : '✓'}
                  </span>

                  {/**
                   * `--mdx-row-identity` — the system's most common row basis at
                   * 94 uses, and this row is exactly what it names: an identity
                   * column that holds the line at 1440 and wraps its buttons
                   * underneath at 375. The export says 190px; the token is 200,
                   * and a second answer to a measured question is worth less
                   * than ten pixels (DECISION-064's precedent for a near miss).
                   */}
                  <span className="flex min-w-0 flex-1 basis-mdx-row-identity flex-col">
                    <span className="text-mdx-15 font-mdx-semibold">
                      {labelFor(credential.provider)}
                    </span>
                    <span className="text-mdx-13 tabular-nums text-mdx-slate">
                      {detailOf(credential, now)}
                    </span>
                    {due ? (
                      <span className="mt-mdx-2 inline-flex items-center gap-mdx-8 text-mdx-13 text-mdx-review-ink">
                        <span
                          aria-hidden="true"
                          className="size-mdx-8 shrink-0 rounded-mdx-pill bg-mdx-review"
                        />
                        Last rotated {ago(due, now)}
                      </span>
                    ) : null}
                  </span>

                  <span className="flex shrink-0 items-center gap-mdx-8">
                    <Button size="small" variant="secondary" onClick={() => replace(credential)}>
                      Replace
                    </Button>
                    <Button
                      size="small"
                      variant="danger-outline"
                      loading={busyId === credential.id ? 'Removing…' : undefined}
                      onClick={() => void remove(credential)}
                    >
                      Delete
                    </Button>
                  </span>
                </li>
              )
            })}
          </ul>
        )}

        {/**
         * The check — manifest B20, Story 18.5, and NOT rendered before it.
         *
         * `settings-credentials.html` has drawn this since the first ingest and
         * DECISION-016 kept it off the screen until the route behind it
         * existed: "render a later milestone's feature not at all, rather than
         * dead". The route exists now, so it renders now — and the e2e test
         * that asserted its ABSENCE moves in the same commit, which is the same
         * discipline `nav.ts`'s `NOT_YET` list enforces for Stories 22.5 and
         * 23.6.
         *
         * Only with a credential to check. With none, the tier runs on
         * Leadgible's own model and there is nothing here for an operator to
         * decide — the empty state above already says so.
         */}
        {rows.length > 0 ? <ModelCheck credentialId={rows[0]!.id} orgId={orgId} /> : null}
      </Card>
    </>
  )
}

/**
 * `Check this model works` — manifest B20, Story 18.5.
 *
 * PRD §3.12: "**Validation button** fires a small test extraction and confirms
 * schema-conforming JSON — not every model supports strict JSON schema output,
 * which extraction depends on."
 *
 * ────────────────────────────────────────────────────────────────────────────
 * A FAILED CHECK IS AMBER, NOT RED, AND THE MOCKUP DRAWS NEITHER
 * ────────────────────────────────────────────────────────────────────────────
 * The export draws only the SUCCESS row — a hot-tint band with a filled ✓, the
 * sentence, and `model · checked just now` pushed to the end. The failure has
 * no drawing, so it mirrors that geometry exactly and swaps the hot triad for
 * the review one.
 *
 * Amber rather than `--mdx-error` because RULE-002 and PRD §1.9 reserve red for
 * "errors and destructive actions", and a model that cannot hold a schema is
 * NEITHER — the check ran, the answer arrived, and the answer is no. Colouring
 * it red would say Leadgible broke, which is the same mistake the route avoids
 * by returning 200 with a reason instead of a 502. It is the argument
 * `Callout`'s notice tone already records: "nothing has broken and nothing is
 * lost."
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE MODEL IS PICKED FROM WHAT THE ENDPOINT ACTUALLY OFFERS
 * ────────────────────────────────────────────────────────────────────────────
 * `GET …/models` lists them and marks the curated set from `config`, so the
 * select is the customer's own inventory rather than a list we imagined. A
 * listing that fails is not an error state for this card: the button stays,
 * with the model typed in — the operator knows their model id even when their
 * endpoint will not enumerate.
 */
function ModelCheck({ credentialId, orgId }: { credentialId: string; orgId: string | null }) {
  const [models, setModels] = useState<{ id: string; known_good: boolean }[]>([])
  const [model, setModel] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{
    ok: boolean
    model: string
    latencyMs: number
    reason?: string
  } | null>(null)

  useEffect(() => {
    if (!orgId) return
    let live = true
    void (async () => {
      try {
        const body = await apiGet<{ models: { id: string; known_good: boolean }[] }>(
          `/v1/orgs/${orgId}/credentials/${credentialId}/models`,
        )
        if (!live) return
        setModels(body.models)
        // The first known-good entry, because "which should I pick" is the
        // question the curated set exists to answer.
        setModel(body.models.find((m) => m.known_good)?.id ?? body.models[0]?.id ?? '')
      } catch {
        // Deliberately silent: see the header. A listing an endpoint declines
        // to give is not a reason to take the button away.
        if (live) setModels([])
      }
    })()
    return () => {
      live = false
    }
  }, [orgId, credentialId])

  async function check() {
    if (!orgId || busy || model.trim() === '') return
    setBusy(true)
    setResult(null)
    try {
      const body = await apiPost<{
        check: { ok: boolean; model: string; latencyMs: number; reason?: string }
      }>(`/v1/orgs/${orgId}/credentials/${credentialId}/check-model`, { model })
      setResult(body.check)
    } catch (error) {
      setResult({
        ok: false,
        model,
        latencyMs: 0,
        reason:
          error instanceof ApiFailure
            ? error.message
            : "We couldn't run the check just now. Nothing was changed — try again in a moment.",
      } as { ok: boolean; model: string; latencyMs: number; reason?: string })
    } finally {
      setBusy(false)
    }
  }

  const tone = result?.ok
    ? { field: 'bg-mdx-hot-tint', mark: 'bg-mdx-hot', ink: 'text-mdx-hot-ink', glyph: '✓' }
    : { field: 'bg-mdx-review-tint', mark: 'bg-mdx-review', ink: 'text-mdx-review-ink', glyph: '!' }

  return (
    <div className="flex flex-col gap-mdx-12 border-t border-mdx-line-soft pt-mdx-20">
      <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-12">
        <Button variant="secondary" loading={busy ? 'Checking…' : undefined} onClick={() => void check()}>
          Check this model works
        </Button>

        {models.length > 0 ? (
          <Select
            label="Model to check"
            value={model}
            onChange={(event) => setModel(event.target.value)}
          >
            {models.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.id}
                {entry.known_good ? ' — known good' : ''}
              </option>
            ))}
          </Select>
        ) : null}

        <span className="min-w-0 flex-1 basis-mdx-measure-34 text-mdx-13 text-mdx-slate">
          Runs one small test extraction and confirms the model returns valid structured output.
        </span>
      </div>

      {result ? (
        <div
          role="status"
          className={`flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-12 rounded-mdx-control px-mdx-16 py-mdx-12 ${tone.field}`}
        >
          <span
            aria-hidden="true"
            className={`flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm text-mdx-14 font-mdx-bold text-mdx-white ${tone.mark}`}
          >
            {tone.glyph}
          </span>
          {/*
            `basis-mdx-measure-34` IS LOAD-BEARING, and 375px is where that
            shows. The export draws only the SUCCESS row — "Returned valid
            output in 0.8s." — which is short enough to sit beside the meta at
            any width. A failure is three sentences, and with `flex-1` alone the
            meta took its natural width first and squeezed the message to one
            word per line, overlapping it. A real basis makes the message demand
            a reading measure and pushes the meta onto its own line instead,
            which is the same wrap the button row above already uses.

            Found by opening the 375px screenshot, not by a test.
          */}
          <span className={`min-w-0 flex-1 basis-mdx-measure-34 text-mdx-15 tabular-nums ${tone.ink}`}>
            {result.ok
              ? `Returned valid output in ${(result.latencyMs / 1000).toFixed(1)}s.`
              : result.reason}
          </span>
          <span className={`ml-auto shrink-0 text-mdx-13 ${tone.ink}`}>
            {result.model} · checked just now
          </span>
        </div>
      ) : null}
    </div>
  )
}
